/**
 * DAF monthly report — generates a Messagerie message from Tristan Midal
 * (DAF) summarizing the current IS situation and ranking the TOP 3 CAPEX
 * opportunities by projected IS savings on a 24-month horizon.
 *
 * Gated by the same `fiscal_simulator` flag as the FiscalSimulator and
 * CapexSimulator components — i.e. requires Tristan Midal hired.
 *
 * Computed lazily at monthly close, NOT in render. Returns a payload
 * ready for `pushCustomMessage(...)` or `null` if there's nothing to say
 * (no profit, no opportunities, DAF not hired).
 *
 * 28/05/2026 v2 — Phase B P1 wrap-up.
 */
import { calculateIS } from '../../data/taxes';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';
import { INVESTMENTS } from '../../data/investments';
import { OFFICE_UPGRADES } from '../../data/officeUpgrades';
import {
  INVESTMENT_CAPEX_CATEGORY,
  OFFICE_UPGRADE_CAPEX_CATEGORY,
  CAPEX_DURATIONS,
  CAPEX_CATEGORY_LABELS,
} from '../../utils/capexDepreciation';

const HORIZON_MONTHS = 24;
const TOP_N = 3;
const MIN_PROFIT_FOR_REPORT = 5000;  // < 5k€ annualisé : pas la peine
const MIN_IS_SAVED_TO_SHOW = 50;     // < 50 € d'éco IS : on ne propose pas

/**
 * Build the list of CAPEX opportunities the player could buy. Returns
 * items with `{ id, label, category, price, monthlyDot, dotInHorizon }`.
 */
function buildOpportunities(state) {
  const owned = state.activeInvestments || {};
  const officeOwned = state.officeUpgrades || {};
  const treasury = state.treasury || 0;
  const items = [];

  for (const inv of INVESTMENTS) {
    const cat = INVESTMENT_CAPEX_CATEGORY[inv.id];
    if (!cat) continue;
    if (!inv.costBuy || inv.costBuy <= 0) continue;
    if (owned[inv.id]) continue;
    // Skip un-affordable items beyond 2× current treasury — won't be
    // realistic short-term suggestions.
    if (inv.costBuy > treasury * 2) continue;
    const months = CAPEX_DURATIONS[cat];
    const monthlyDot = inv.costBuy / months;
    items.push({
      id: inv.id, label: inv.name, category: cat, price: inv.costBuy,
      monthlyDot, dotInHorizon: monthlyDot * Math.min(HORIZON_MONTHS, months),
    });
  }
  for (const upg of OFFICE_UPGRADES) {
    const cat = OFFICE_UPGRADE_CAPEX_CATEGORY[upg.id];
    if (!cat) continue;
    if (!upg.costBuy || upg.costBuy <= 0) continue;
    if (officeOwned[upg.id]) continue;
    if (upg.costBuy > treasury * 2) continue;
    const months = CAPEX_DURATIONS[cat];
    const monthlyDot = upg.costBuy / months;
    items.push({
      id: upg.id, label: upg.name, category: cat, price: upg.costBuy,
      monthlyDot, dotInHorizon: monthlyDot * Math.min(HORIZON_MONTHS, months),
    });
  }
  return items;
}

/**
 * Compute the IS saved over the horizon for a given monthly extra dotation.
 * Mirrors the math used by CapexSimulator → keeps the two consistent.
 */
function computeIsSaved(monthlyProfit, monthlyExtraDot, horizonMonths) {
  if (horizonMonths <= 0 || monthlyProfit <= 0) return 0;
  let isBase = 0;
  let isWith = 0;
  for (let m = 0; m < horizonMonths; m += 12) {
    const monthsInYear = Math.min(12, horizonMonths - m);
    const annualBase = monthlyProfit * 12;
    const annualWith = Math.max(0, (monthlyProfit - monthlyExtraDot) * 12);
    isBase += calculateIS(annualBase) * (monthsInYear / 12);
    isWith += calculateIS(annualWith) * (monthsInYear / 12);
  }
  return Math.max(0, Math.round(isBase - isWith));
}

function formatMoney(n) {
  return `${Math.round(n).toLocaleString('fr-FR')} €`;
}

/**
 * Main entry — return a Messagerie payload OR null.
 * @param {object} state  the live Zustand state slice
 * @param {function} hasSupportUnlock  state.hasSupportUnlock — the gating helper
 */
export function buildDafMonthlyReport(state, hasSupportUnlock) {
  if (typeof hasSupportUnlock !== 'function') return null;
  if (!hasSupportUnlock('fiscal_simulator')) return null;

  const month = state.month || 1;
  const annualProfit = state.annualProfit || 0;
  // 12/02/2026 — Utilise le lissage début d'année (projectAnnualProfit).
  const annualisedProfit = projectAnnualProfit({ annualProfit, month }).annualProfit;
  if (annualisedProfit < MIN_PROFIT_FOR_REPORT) return null;

  const monthlyProfit = annualisedProfit / 12;
  const isAnnual = Math.round(calculateIS(Math.max(0, annualisedProfit)));

  const opportunities = buildOpportunities(state);
  const ranked = opportunities
    .map(o => ({ ...o, isSaved: computeIsSaved(monthlyProfit, o.monthlyDot, HORIZON_MONTHS) }))
    .filter(o => o.isSaved >= MIN_IS_SAVED_TO_SHOW)
    .sort((a, b) => b.isSaved - a.isSaved)
    .slice(0, TOP_N);

  if (ranked.length === 0) return null;

  // Build a markdown-friendly body. The Messagerie renderer will parse
  // `**bold**` and `\n` safely via MarkdownLite.
  const bullets = ranked.map((o, i) => {
    const netCost = Math.max(0, o.price - o.isSaved);
    return `${i + 1}. **${o.label}** (${CAPEX_CATEGORY_LABELS[o.category]}, ${formatMoney(o.price)}) → économie IS estimée **${formatMoney(o.isSaved)}** sur 24 mois · coût net **${formatMoney(netCost)}**`;
  }).join('\n');

  const body =
    `Bonjour,\n\n` +
    `Petit point de pilotage fiscal pour ce mois-ci.\n\n` +
    `**Situation actuelle :** votre bénéfice annualisé est estimé à **${formatMoney(annualisedProfit)}/an**, soit un IS prévisionnel d'environ **${formatMoney(isAnnual)}** sur l'exercice.\n\n` +
    `**Top ${ranked.length} opportunité${ranked.length > 1 ? 's' : ''} d'investissement** (classées par économie d'IS sur 24 mois) :\n\n` +
    `${bullets}\n\n` +
    `Pour creuser une simulation précise, rendez-vous dans **Fiscalité → Impôt Sociétés → Simulateur CAPEX**.\n\n` +
    `Cordialement,\nTristan Midal, Directeur administratif et financier`;

  return {
    senderType: 'support',
    senderName: 'Tristan Midal (DAF)',
    senderRole: 'role-pierre-marchetti',
    tone: 'advice',
    kind: 'info',
    subject: `Pilotage fiscal — ${ranked.length} opportunité${ranked.length > 1 ? 's' : ''} CAPEX à étudier`,
    body,
    ttlDays: 30,
  };
}
